/**
 * Static lookup tables for the Health module: source colors, labels, default starred
 * metrics per provider, and the selectable wearable options.
 */

// Neutral color palette for metric sources — Xavior blue primary, muted secondary
export const SOURCE_COLORS: Record<string, { bg: string; fg: string; accent: string }> = {
  // Whoop — blue
  'whoop:strain':   { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'whoop:recovery': { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'whoop:sleep':    { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'whoop:workout':  { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'whoop:body':     { bg: 'rgba(107, 114, 128, 0.06)', fg: '#6b7280', accent: 'rgba(107, 114, 128, 0.12)' },
  'whoop:heart':    { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'whoop:activity': { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  // Oura — warm gray/charcoal
  'oura:sleep':     { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:readiness': { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:activity':  { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:heart':     { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:recovery':  { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:body':      { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  'oura:stress':    { bg: 'rgba(89, 89, 89, 0.06)',  fg: '#595959', accent: 'rgba(89, 89, 89, 0.12)' },
  // Strava — orange
  'strava:activity':{ bg: 'rgba(252, 82, 0, 0.06)',  fg: '#FC5200', accent: 'rgba(252, 82, 0, 0.12)' },
  'strava:workout': { bg: 'rgba(252, 82, 0, 0.06)',  fg: '#FC5200', accent: 'rgba(252, 82, 0, 0.12)' },
  'strava:heart':   { bg: 'rgba(252, 82, 0, 0.06)',  fg: '#FC5200', accent: 'rgba(252, 82, 0, 0.12)' },
  // Fitbit — teal
  'fitbit:sleep':   { bg: 'rgba(0, 176, 185, 0.06)', fg: '#00B0B9', accent: 'rgba(0, 176, 185, 0.12)' },
  'fitbit:activity':{ bg: 'rgba(0, 176, 185, 0.06)', fg: '#00B0B9', accent: 'rgba(0, 176, 185, 0.12)' },
  'fitbit:heart':   { bg: 'rgba(0, 176, 185, 0.06)', fg: '#00B0B9', accent: 'rgba(0, 176, 185, 0.12)' },
  'fitbit:body':    { bg: 'rgba(0, 176, 185, 0.06)', fg: '#00B0B9', accent: 'rgba(0, 176, 185, 0.12)' },
  // Manual / health sources
  'health:food':    { bg: 'rgba(107, 114, 128, 0.06)', fg: '#6b7280', accent: 'rgba(107, 114, 128, 0.12)' },
  'health:exercise':{ bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'calories':       { bg: 'rgba(249, 115, 22, 0.06)',  fg: '#f97316', accent: 'rgba(249, 115, 22, 0.12)' },
  'protein':        { bg: 'rgba(139, 92, 246, 0.06)',  fg: '#8b5cf6', accent: 'rgba(139, 92, 246, 0.12)' },
  'exercise':       { bg: 'rgba(34, 197, 94, 0.06)',   fg: '#22c55e', accent: 'rgba(34, 197, 94, 0.12)' },
  'weight':         { bg: 'rgba(14, 165, 233, 0.06)',  fg: '#0ea5e9', accent: 'rgba(14, 165, 233, 0.12)' },
  'systolic':       { bg: 'rgba(239, 68, 68, 0.06)',   fg: '#ef4444', accent: 'rgba(239, 68, 68, 0.12)' },
  'diastolic':      { bg: 'rgba(249, 115, 22, 0.06)', fg: '#f97316', accent: 'rgba(249, 115, 22, 0.12)' },
  default:          { bg: 'rgba(107, 114, 128, 0.06)', fg: '#6b7280', accent: 'rgba(107, 114, 128, 0.12)' },
};

export const PROVIDER_COLORS: Record<string, string> = {
  oura: '#2f855a',
  whoop: '#d53f8c',
  fitbit: '#3182ce',
  strava: '#e53e3e',
};

export const SOURCE_LABELS: Record<string, string> = {
  'whoop:strain': 'Strain',
  'whoop:recovery': 'Recovery',
  'whoop:sleep': 'Sleep',
  'whoop:workout': 'Workout',
  'whoop:body': 'Body',
  'whoop:heart': 'Heart',
  'whoop:activity': 'Activity',
  // Whoop metric-level labels
  strain: 'Day Strain',
  recoveryScore: 'Recovery',
  hrv: 'HRV',
  sleepPerformance: 'Sleep Score',
  sleepConsistency: 'Sleep Consistency',
  sleepEfficiency: 'Sleep Efficiency',
  respiratoryRate: 'Respiratory Rate',
  skinTemp: 'Skin Temp',
  spo2: 'SpO2',
  workoutStrain: 'Workout Strain',
  workoutCalories: 'Workout Cal',
  workoutAvgHr: 'Workout Avg HR',
  workoutMaxHr: 'Workout Max HR',
  workoutDistance: 'Workout Distance',
  zoneRest: 'Zone Rest',
  zoneFatBurn: 'Zone Fat Burn',
  zoneCardio: 'Zone Cardio',
  zonePeak: 'Zone Peak',
  lightSleep: 'Light Sleep',
  awakeTime: 'Awake Time',
  disturbances: 'Disturbances',
  maxHeartRateBody: 'Max HR (Body)',
  avgHeartRate: 'Avg Heart Rate',
  maxHeartRate: 'Max Heart Rate',
  // Oura category-level (fallback)
  'oura:sleep': 'Sleep',
  'oura:readiness': 'Readiness',
  'oura:activity': 'Activity',
  'oura:heart': 'Heart Rate',
  'oura:recovery': 'Recovery',
  'oura:body': 'Body',
  // Oura metric-level (used for Trends chart)
  readinessScore: 'Readiness',
  sleepScore: 'Sleep',
  activityScore: 'Activity',
  heartRate: 'Heart Rate',
  spo2Average: 'SpO2',
  steps: 'Steps',
  activeCalories: 'Active Cal',
  totalCalories: 'Total Cal',
  equivalentWalkingDistance: 'Distance',
  sleepDuration: 'Sleep Duration',
  sleepDurationMs: 'Sleep Duration',
  sleepEfficiencyPct: 'Sleep Efficiency',
  sleepAverageHRV: 'Avg HRV',
  sleepAverageHR: 'Avg HR (Sleep)',
  sleepLowestHR: 'Lowest HR',
  deepSleep: 'Deep Sleep',
  remSleep: 'REM Sleep',
  sleepLatency: 'Sleep Latency',
  sleepRestfulness: 'Restfulness',
  sleepTiming: 'Sleep Timing',
  totalSleep: 'Total Sleep',
  temperatureDeviation: 'Temp Deviation',
  temperatureTrendDeviation: 'Temp Trend',
  highActivityTime: 'High Activity',
  mediumActivityTime: 'Medium Activity',
  lowActivityTime: 'Low Activity',
  sedentaryTime: 'Sedentary',
  restingTime: 'Resting',
  sleepDeepDuration: 'Deep Sleep (min)',
  sleepRemDuration: 'REM (min)',
  sleepLightDuration: 'Light Sleep (min)',
  sleepAwakeTime: 'Awake Time',
  sleepRestlessPeriods: 'Restless Periods',
  sleepAverageBreathRate: 'Breath Rate',
  stressHigh: 'Stress',
  recoveryHigh: 'Recovery Time',
  // Strava
  'strava:activity': 'Activity',
  'strava:workout': 'Workout',
  'strava:heart': 'Heart Rate',
  // Fitbit
  'fitbit:sleep': 'Sleep',
  'fitbit:activity': 'Activity',
  'fitbit:heart': 'Heart Rate',
  'fitbit:body': 'Body',
  'health:food': 'Food',
  'health:exercise': 'Exercise',
  'calories': 'Calories',
  'protein': 'Protein',
  'exercise': 'Exercise',
  'weight': 'Weight',
  'systolic': 'Systolic BP',
  'diastolic': 'Diastolic BP',
  resilienceLevel: 'Resilience',
  vascularAge: 'Cardiovascular Age',
  vo2Max: 'VO2 Max',
  workoutDuration: 'Workout Duration',
  workoutIntensity: 'Workout Intensity',
  sessionHeartRate: 'Session HR',
  sessionHRV: 'Session HRV',
  minutesAsleep: 'Minutes Asleep',
  minutesAwake: 'Minutes Awake',
  activeMinutes: 'Active Minutes',
  sedentaryMinutes: 'Sedentary Minutes',
  floors: 'Floors',
  bmi: 'BMI',
  bodyFat: 'Body Fat',
  hrvDailyRmssd: 'HRV (RMSSD)',
  skinTemperature: 'Skin Temp',
  cardioFitnessScore: 'Cardio Score',
  restingHeartRate: 'Resting HR',
  movingTime: 'Moving Time',
  elapsedTime: 'Elapsed Time',
  totalElevationGain: 'Elevation Gain',
  avgSpeed: 'Avg Speed',
  maxSpeed: 'Max Speed',
  avgHeartrate: 'Avg Heart Rate',
  maxHeartrate: 'Max Heart Rate',
  stravaCalories: 'Calories (Strava)',
  // Fitbit additional
  activeZoneMinutes: 'Active Zone Min',
  fatBurnMinutes: 'Fat Burn Zone',
  cardioMinutes: 'Cardio Zone',
  peakMinutes: 'Peak Zone',
  spo2Min: 'SpO2 Min',
  // Strava additional
  averageWatts: 'Avg Power',
  maxWatts: 'Max Power',
  weightedAvgWatts: 'Normalized Power',
  averageCadence: 'Cadence',
  sufferScore: 'Suffer Score',
  elevHigh: 'Max Elevation',
  elevLow: 'Min Elevation',
  activityName: 'Activity',
  activityType: 'Type',
  averageSpeed: 'Avg Speed',
  averageHeartrate: 'Avg HR',
  elevationGain: 'Elevation Gain',
  allTimeRideCount: 'Total Rides',
  allTimeRideDistance: 'Total Ride Distance',
  allTimeRunCount: 'Total Runs',
  allTimeRunDistance: 'Total Run Distance',
  allTimeSwimCount: 'Total Swims',
  allTimeSwimDistance: 'Total Swim Distance',
  biggestRideDistance: 'Biggest Ride',
  biggestClimbElevation: 'Biggest Climb',
};

export const PROVIDER_DEFAULT_STARS: Record<string, string[]> = {
  oura: ['readinessScore', 'sleepScore', 'activityScore', 'heartRate', 'stressHigh'],
  whoop: ['strain', 'recoveryScore', 'sleepPerformance', 'hrv', 'restingHeartRate'],
  fitbit: ['steps', 'sleepDuration', 'restingHeartRate', 'activeMinutes', 'calories'],
  strava: ['distance', 'movingTime', 'avgHeartrate', 'totalElevationGain', 'calories'],
};

export const WEARABLE_OPTIONS = [
  {
    provider: 'oura', name: 'Oura', color: '#595959', desc: 'Sleep, readiness, activity',
    logo: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2.5"/><circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2"/></svg>,
  },
  {
    provider: 'whoop', name: 'WHOOP', color: 'var(--color-text)', desc: 'Sleep, recovery, strain',
    logo: <svg width="14" height="10" viewBox="0 0 40 24" fill="none"><path d="M2 2L8 18L14 8L20 18L26 8L32 18L38 2" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  },
  {
    provider: 'strava', name: 'Strava', color: '#FC5200', desc: 'Runs, rides, activities',
    logo: <svg width="12" height="14" viewBox="0 0 24 28" fill="none"><path d="M15.387 20.944L13.298 16.828H10.233L15.387 27L20.537 16.828H17.471L15.387 20.944Z" fill="white"/><path d="M10.463 0L3.463 13.828H7.632L10.463 8.23L13.298 13.828H17.471L10.463 0Z" fill="white" opacity="0.6"/></svg>,
  },
  {
    provider: 'fitbit', name: 'Fitbit', color: '#00B0B9', desc: 'Steps, heart rate, sleep',
    logo: <svg width="12" height="14" viewBox="0 0 24 28" fill="none"><circle cx="12" cy="3" r="2.2" fill="white"/><circle cx="12" cy="10" r="2.8" fill="white"/><circle cx="12" cy="18" r="2.8" fill="white"/><circle cx="12" cy="25" r="2.2" fill="white"/><circle cx="6" cy="10" r="1.8" fill="white"/><circle cx="18" cy="10" r="1.8" fill="white"/><circle cx="6" cy="18" r="1.8" fill="white"/><circle cx="18" cy="18" r="1.8" fill="white"/></svg>,
  },
];
